"use client";
import React from "react";
import { mockProperties } from "../../constants/mockProperties";
import dayjs from "dayjs";
import DynamicSlider from "./components/DynamicSlider";
import { Currency, PropertyType } from "src/types";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";
import ContactMandate from "./components/ContactMandate";
import { TeamCardWhatsapp } from "./components/TeamCard";
import SimilarProperties from "./components/SimilarProperties";
import { BranchSummaryCard } from "./Branches";
import PropertyUtilities from "../../constants/utilitiesIcons";
import { ProfileSummary } from "../../constants/branchData";
import { PriceCard } from "./PropertyPage1";
import { ParentCard } from "./PropertyPage1";
import { Info } from "./PropertyPage1";
import Example from "./components/Map";
import { ShowMoreText } from "./About1";
import useCompanyListingDetails from "src/app/lib/useFecthCompanyListing";
import { useParams, useRouter } from "next/navigation";
import { getCurrencySign } from "src/app/lib/getCurrencySign";
import PropertyPageSkeleton from "./components/skeletons/PropertyPage";
import {
  formatTenantFields,
  newTenantsFields,
  renewalTenantsFields,
} from "src/app/serverUtils";
import BackBtn from "../design3/components/BackBtn3";

export function formatCurrency(currency: Currency, value?: string | number) {
  if (value === undefined || value === null || value === "") {
    return `0`;
  }
  if (value === "0.00") {
    return "0";
  }

  if (typeof value === "number") {
    return `${currency}${value.toLocaleString()}`;
  }

  const num = Number(value);
  return isNaN(num) ? `0` : `${currency}${num.toLocaleString()}`;
}
export const propertyData: ProfileSummary = {
  title: "Property Information",
  values: {
    property_title: "Obolongo Estate",
    category: "Residential",
    preference: "Newly Built",
    unit_type: "Block of Flat",
    unit_sub_type: "Block",
    bathroom: 21,
    toilet: 21,
    total_unit: 43,
    total_area: "4354 sqm",
    offer: 21,
    total_views: 121,
    bookmarks: 21,
    added_date: "21/01/2025",
    last_updated: "21/01/2025",
  },
};
export function camelCaseToWords(camelCaseStr: string): string {
  return camelCaseStr
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (char) => char.toUpperCase());
}
dayjs.extend(relativeTime);
dayjs.extend(utc);
const PropertyPage1 = () => {
  const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
  const search = useParams();
  const router = useRouter();
  const { id } = search;
  const toNum = parseInt(id as string);
  const {
    data: item,
    isError,
    isLoading,
  } = useCompanyListingDetails(toNum) as {
    data: PropertyType | undefined;
    isError: boolean;
    isLoading: boolean;
  };

  if (isLoading && !isDemo) {
    return <PropertyPageSkeleton />;
  }
  if (isError && !isDemo) {
    return <div className="mt-4">Error occurred</div>;
  }

  const data = isDemo ? mockProperties.forRent[0] : item;
  if (!data && !isDemo && !isLoading) {
    return <div className="mt-4">Not available</div>;
  }
  if (!data && !isLoading) {
    return <div className="mt-4">Data not available</div>;
  }
  const currency = getCurrencySign(data?.currency || "naira");
  const newTenantsData = formatTenantFields(
    item as PropertyType,
    currency as unknown as Currency,
    newTenantsFields
  );
  const renewalTenantsData = formatTenantFields(
    item as PropertyType,
    currency as unknown as Currency,
    renewalTenantsFields
  );

  if (data) {
    return (
      <section className="py-10 w-full">
        <div className="wrapper">
          <div
            onClick={() => {
              router.back();
            }}
            className="back mb-4"
          >
            <BackBtn />
          </div>
          <div className="grid-container relative">
            <div className="item flex flex-col gap-10">
              <div className="carousel w-auto">
                <DynamicSlider
                  loading={isLoading}
                  url={data?.videoLink}
                  slides={[...(data?.images || [])]}
                />
              </div>
              <Info
                address={data?.address || "No address available"}
                category={data?.category}
                id={data?.propertyId as number}
                title={data.title}
              />
              <ParentCard header="Property Brief">
                <ShowMoreText
                  loading={isLoading}
                  maxLength={300}
                  text={data.propertyDesc || "Description is not available"}
                />
              </ParentCard>
              {data.facilities && data.facilities?.length > 0 && (
                <ParentCard header="Facilities">
                  <PropertyUtilities
                    selectedItems={data.facilities as string[]}
                  />
                </ParentCard>
              )}

              <PropertyInfo
                title="Property Information"
                details={[
                  { key: "Property Title", value: data.secondTitle },
                  // { key: "Unit Name/Number", value: data.unit_name || "--" },
                  { key: "Category", value: data.category },
                  { key: "Preference", value: data.unitPreference },
                  { key: "Unit Type", value: data.newType },
                  { key: "Unit Sub Type", value: data.unitSubtype },
                  {
                    key: "Total Area",
                    value: `${data.size?.toLocaleString()} sqm `,
                  },
                  {
                    pair: [
                      { key: "Bedroom", value: data.bedroom || "--" },
                      { key: "Bathroom", value: data.bathroom || "--" },
                    ],
                  },
                  {
                    pair: [
                      { key: "Toilet", value: data.toilet || "--" },
                      { key: "Total Unit", value: data.total_units || 0 },
                    ],
                  },
                  {
                    pair: [
                      {
                        key: "Unit Name/Number",
                        value: data.unit_name as string,
                      },
                      {
                        key: "Available Unit",
                        value: data.total_vacant || "--",
                      },
                    ],
                  },
                  {
                    pair: [
                      { key: "Total Views", value: data.totalViews || "--" },
                      { key: "Bookmarks", value: data.bookMarks || 0 },
                    ],
                  },
                  {
                    pair: [
                      {
                        key: "Added Date",
                        value:
                          dayjs(data.addedDate).format("DD/MM/YYYY") || "--",
                      },
                      {
                        key: "Last Updated",
                        value:
                          dayjs.utc(data.lastUpdated).local().fromNow() || "--",
                      },
                    ],
                  },
                ]}
              />
              <ParentCard className="mb-0" header="New Tenants">
                <BranchSummaryCard
                  className="!shadow-none"
                  variant
                  noHeader
                  {...{
                    title: "New Tenants",
                    values: newTenantsData,
                  }}
                />
              </ParentCard>
              <ParentCard header="Renewal Tenants">
                <BranchSummaryCard
                  className="!shadow-none"
                  variant
                  noHeader
                  {...{
                    title: "",
                    values: renewalTenantsData,
                  }}
                />
              </ParentCard>
              {data?.coordinate && (
                <ParentCard header="Location">
                  <Example coordinates={data?.coordinate} />
                </ParentCard>
              )}
            </div>
            <div id="child" className="w-ful sticky top-0 flex flex-col gap-14">
              <PriceCard {...data} />
              <div className="card mt-24">
                {data.staff ? (
                  <TeamCardWhatsapp {...data.staff} />
                ) : (
                  <div>Staff not available</div>
                )}
              </div>
              <ContactMandate email={data.staff?.email as string} />
            </div>
          </div>
          {data.staffListing && data.staffListing.length > 0 ? (
            <SimilarProperties arr={data.staffListing} />
          ) : (
            <div></div>
          )}
        </div>
      </section>
    );
  }
};
export default PropertyPage1;
type PropertyItem = { key: string; value: string | number };
type PropertyData = { pair?: PropertyItem[]; key?: string; value?: string };

interface PropertyInfoProps {
  details: PropertyData[];
  title: string;
}
const banned = ["0", 0, "None", "none", "--", "__"];
export const PropertyInfo: React.FC<PropertyInfoProps> = ({
  details,
  title,
}) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      {/* Title */}
      <h2 className=" h5 !font-bold border-b border-b-disabled-main/50 pb-3 text-secondary-main font-semibold mb-4">
        {title}
      </h2>

      <div className="space-y-3">
        {details
          .map((item, index) => {
            if (item.pair) {
              // Filter out banned values in the pair
              const filteredPair = item.pair.filter(
                (pairItem) => !banned.includes(pairItem.value)
              );
              // Skip rendering if both pair items are banned
              if (filteredPair.length === 0) return null;
              return (
                <div key={index} className="flex justify-between gap-4">
                  {filteredPair.map((pairItem, subIndex) => (
                    <div
                      key={subIndex}
                      className={`font-medium pb-2 border-b ${
                        filteredPair.length === 1 ? "basis-full" : "basis-1/2"
                      }`}
                    >
                      <span className="font-medium text-lg text-secondary-main">
                        {pairItem.key}:{" "}
                      </span>
                      <span className="capitalize">{pairItem.value}</span>
                    </div>
                  ))}
                </div>
              );
            } else {
              // Skip rendering if single item is banned
              if (banned.includes(item.value!)) return null;

              return (
                <div key={index} className="border-b pb-2">
                  <span className="font-medium text-lg text-secondary-main">
                    {item.key}:{" "}
                  </span>
                  <span className="capitalize">{item.value}</span>
                </div>
              );
            }
          })
          .filter(Boolean)}
      </div>
    </div>
  );
};
